import React, { useMemo } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
   Box,
   Typography,
   Card,
   CardContent,
   TextField,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
} from '@mui/material';
import {
   Lock as LockIcon,
   Public as PublicIcon,
   AttachMoney as MoneyIcon,
   Favorite as FavoriteIcon,
   Check as CheckIcon,
   People as PeopleIcon,
} from '@mui/icons-material';
import type { CreateAlbumWizardApi } from './useCreateAlbumWizard';
import type { CreateAlbumWizardAlbumType } from './createAlbumWizardTypes';

interface CreateAlbumWizardPanelsProps {
   wizard: CreateAlbumWizardApi;
}

const CreateAlbumWizardPanels: React.FC<CreateAlbumWizardPanelsProps> = ({ wizard }) => {
   const theme = useTheme();
   const primaryMain = theme.palette.primary.main;

   const {
      activeStep,
      selectedType,
      setSelectedType,
      title,
      setTitle,
      description,
      setDescription,
      albumDate,
      setAlbumDate,
      showTitleError,
      setShowTitleError,
      pricePerPhoto,
      setPricePerPhoto,
      pricingModel,
      setPricingModel,
      paidAlbumAccessType,
      setPaidAlbumAccessType,
   } = wizard;

   const albumTypes = useMemo(
      () =>
         [
            {
               id: 'private' as CreateAlbumWizardAlbumType,
               title: 'Private Album',
               description:
                  'Invite-only with participant approval. Perfect for personal events and private gatherings.',
               icon: <LockIcon sx={{ fontSize: 40, color: primaryMain }} />,
               features: ['QR code access', 'Participant approvals', 'Controlled sharing'],
               disabled: false,
            },
            {
               id: 'public' as CreateAlbumWizardAlbumType,
               title: 'Public Album',
               description:
                  'Anyone with the access code can view and upload photos. Great for open events.',
               icon: <PublicIcon sx={{ fontSize: 40, color: primaryMain }} />,
               features: ['Public link sharing', 'Anyone can upload', 'All contributors displayed'],
               disabled: false,
            },
            {
               id: 'paid' as CreateAlbumWizardAlbumType,
               title: 'Paid Album',
               description:
                  'Photographer sets price per photo. Photos shown with watermark until purchased.',
               icon: <MoneyIcon sx={{ fontSize: 40, color: primaryMain }} />,
               features: ['Set price per photo', 'Watermarked previews', 'Download after purchase'],
               disabled: false,
            },
         ] as const,
      [primaryMain]
   );

   if (activeStep === 0) {
      return (
         <Box>
            <Typography
               variant='h5'
               sx={{
                  mb: 1,
                  textAlign: 'center',
                  fontWeight: 600,
                  color: 'text.primary',
                  fontFamily: theme.typography.h3.fontFamily,
                  letterSpacing: theme.typography.h3.letterSpacing,
               }}
            >
               Choose Album Type
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3, textAlign: 'center' }}>
               Select the type of album that best fits your needs.
            </Typography>

            <Box
               sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'center',
                  alignItems: 'center',
               }}
            >
               {albumTypes.map((type) => (
                  <Card
                     key={type.id}
                     sx={{
                        cursor: type.disabled ? 'not-allowed' : 'pointer',
                        border: '1px solid',
                        borderColor: selectedType === type.id ? 'primary.main' : 'divider',
                        borderWidth: selectedType === type.id ? 2 : 1,
                        borderRadius: 2,
                        minWidth: { xs: '100%', sm: 200 },
                        maxWidth: { xs: '100%', sm: 250 },
                        width: { xs: '100%', sm: 'auto' },
                        opacity: type.disabled ? 0.5 : 1,
                        '&:hover': {
                           borderColor: type.disabled
                              ? 'divider'
                              : selectedType === type.id
                                ? 'primary.main'
                                : 'rgba(255, 255, 255, 0.22)',
                        },
                        transition: 'all 0.2s ease-in-out',
                        bgcolor: type.disabled
                           ? 'action.disabledBackground'
                           : selectedType === type.id
                             ? (t) => alpha(t.palette.primary.main, 0.12)
                             : (t) => alpha(t.palette.common.white, 0.05),
                     }}
                     onClick={() => !type.disabled && setSelectedType(type.id)}
                  >
                     <CardContent sx={{ p: 3, textAlign: 'center', position: 'relative' }}>
                        {type.disabled && (
                           <Box
                              sx={{
                                 position: 'absolute',
                                 top: 8,
                                 right: 8,
                                 backgroundColor: '#6b7280',
                                 color: 'white',
                                 px: 1,
                                 py: 0.5,
                                 borderRadius: 1,
                                 fontSize: '0.75rem',
                                 fontWeight: 500,
                              }}
                           >
                              Coming Soon
                           </Box>
                        )}
                        {selectedType === type.id && !type.disabled && (
                           <CheckIcon
                              sx={{
                                 color: 'primary.main',
                                 fontSize: 20,
                                 position: 'absolute',
                                 top: 8,
                                 right: 8,
                              }}
                           />
                        )}
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2,
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 width: 48,
                                 height: 48,
                                 bgcolor: 'primary.main',
                                 borderRadius: '50%',
                                 flexShrink: 0,
                              }}
                           >
                              {React.cloneElement(type.icon, {
                                 sx: { color: 'white', fontSize: 24 },
                              })}
                           </Box>
                           <Box sx={{ width: '100%' }}>
                              <Typography
                                 variant='h6'
                                 sx={{
                                    mb: 1,
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    fontSize: '1rem',
                                 }}
                              >
                                 {type.title}
                              </Typography>
                              <Typography
                                 variant='body2'
                                 color='text.secondary'
                                 sx={{ mb: 2, lineHeight: 1.4, fontSize: '0.875rem' }}
                              >
                                 {type.description}
                              </Typography>
                              <Box
                                 sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.5,
                                    alignItems: 'flex-start',
                                 }}
                              >
                                 {type.features.map((feature) => (
                                    <Box
                                       key={feature}
                                       sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                    >
                                       <Box
                                          sx={{
                                             width: 4,
                                             height: 4,
                                             borderRadius: '50%',
                                             bgcolor: 'primary.main',
                                          }}
                                       />
                                       <Typography
                                          variant='body2'
                                          sx={{ fontSize: '0.8rem', color: 'text.secondary' }}
                                       >
                                          {feature}
                                       </Typography>
                                    </Box>
                                 ))}
                              </Box>
                           </Box>
                        </Box>
                     </CardContent>
                  </Card>
               ))}
            </Box>
         </Box>
      );
   }

   if (activeStep === 1) {
      return (
         <Box>
            <Typography
               variant='h5'
               sx={{
                  mb: 1,
                  textAlign: 'center',
                  fontWeight: 600,
                  color: 'text.primary',
                  fontFamily: theme.typography.h3.fontFamily,
                  letterSpacing: theme.typography.h3.letterSpacing,
               }}
            >
               Album Details
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 3, textAlign: 'center' }}>
               Provide basic information about your album.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
               <TextField
                  label='Album Title *'
                  value={title}
                  onChange={(e) => {
                     setTitle(e.target.value);
                     if (showTitleError && e.target.value.trim()) {
                        setShowTitleError(false);
                     }
                  }}
                  fullWidth
                  required
                  placeholder='e.g., Summer Wedding 2025'
                  error={showTitleError}
                  helperText={showTitleError ? 'Album title is required' : ''}
               />

               <TextField
                  label='Description'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  placeholder='Add a description for your album...'
               />

               <TextField
                  label='Album Date'
                  type='date'
                  value={albumDate}
                  onChange={(e) => setAlbumDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{
                     shrink: true,
                  }}
               />
            </Box>
         </Box>
      );
   }

   if (activeStep === 2) {
      if (selectedType === 'public') {
         return (
            <Box>
               <Typography
                  variant='h5'
                  sx={{
                     mb: 1,
                     textAlign: 'center',
                     fontWeight: 600,
                     color: 'text.primary',
                     fontFamily: theme.typography.h3.fontFamily,
                     letterSpacing: theme.typography.h3.letterSpacing,
                  }}
               >
                  Public Album Ready
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 3, textAlign: 'center' }}>
                  Your public album will be accessible to anyone with the link.
               </Typography>

               <Card
                  sx={{
                     p: 3,
                     borderRadius: 2,
                     border: '1px solid',
                     borderColor: 'primary.main',
                     bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                     <PublicIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                     <Box>
                        <Typography
                           variant='subtitle1'
                           sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary' }}
                        >
                           Public Album
                        </Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.875rem' }}>
                           Anyone with the link can view and upload photos to your album
                        </Typography>
                     </Box>
                  </Box>
               </Card>
            </Box>
         );
      }

      if (selectedType === 'private') {
         return (
            <Box>
               <Typography
                  variant='h5'
                  sx={{
                     mb: 1,
                     textAlign: 'center',
                     fontWeight: 600,
                     color: 'text.primary',
                     fontFamily: theme.typography.h3.fontFamily,
                     letterSpacing: theme.typography.h3.letterSpacing,
                  }}
               >
                  Private Album Settings
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 3, textAlign: 'center' }}>
                  You can add participants after creating the album.
               </Typography>

               <Card
                  sx={{
                     p: 3,
                     borderRadius: 2,
                     border: '1px solid',
                     borderColor: 'primary.main',
                     bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                     <PeopleIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                     <Typography
                        variant='subtitle1'
                        sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary' }}
                     >
                        Participant Management
                     </Typography>
                  </Box>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 2, fontSize: '0.875rem' }}>
                     After creating your album, you&apos;ll be able to:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                     {[
                        'Invite participants via email or SMS',
                        'Require approval for new members',
                        'Generate QR codes for easy access',
                        'Control who can view and upload photos',
                     ].map((feature) => (
                        <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <Box
                              sx={{
                                 width: 4,
                                 height: 4,
                                 borderRadius: '50%',
                                 bgcolor: 'primary.main',
                              }}
                           />
                           <Typography variant='body2' sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                              {feature}
                           </Typography>
                        </Box>
                     ))}
                  </Box>
               </Card>
            </Box>
         );
      }

      if (selectedType === 'paid') {
         return (
            <Box>
               <Typography
                  variant='h5'
                  sx={{
                     mb: 1,
                     textAlign: 'center',
                     fontWeight: 600,
                     color: 'text.primary',
                     fontFamily: theme.typography.h3.fontFamily,
                     letterSpacing: theme.typography.h3.letterSpacing,
                  }}
               >
                  Pricing Configuration
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 3, textAlign: 'center' }}>
                  Choose your pricing model and set the price.
               </Typography>

               <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id='access-type-label'>Album Access Type</InputLabel>
                  <Select
                     labelId='access-type-label'
                     value={paidAlbumAccessType}
                     label='Album Access Type'
                     onChange={(e) => setPaidAlbumAccessType(e.target.value as 'public' | 'private')}
                     renderValue={(v) => (v === 'public' ? 'Public Access' : 'Private Access')}
                     MenuProps={{
                        PaperProps: {
                           sx: {
                              maxHeight: 320,
                              bgcolor: 'background.paper',
                              border: 1,
                              borderColor: 'divider',
                              color: 'text.primary',
                              '& .MuiMenuItem-root': { color: 'text.primary' },
                           },
                        },
                     }}
                  >
                     <MenuItem value='public'>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <PublicIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                           <Box>
                              <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
                                 Public Access
                              </Typography>
                              <Typography variant='caption' color='text.secondary'>
                                 Anyone with the link can pay and access
                              </Typography>
                           </Box>
                        </Box>
                     </MenuItem>
                     <MenuItem value='private'>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <LockIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                           <Box>
                              <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
                                 Private Access
                              </Typography>
                              <Typography variant='caption' color='text.secondary'>
                                 Only invited users can pay and access
                              </Typography>
                           </Box>
                        </Box>
                     </MenuItem>
                  </Select>
               </FormControl>

               <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id='pricing-model-label'>Pricing Model</InputLabel>
                  <Select
                     labelId='pricing-model-label'
                     value={pricingModel}
                     label='Pricing Model'
                     onChange={(e) =>
                        setPricingModel(e.target.value as 'per_photo' | 'one_time' | 'donation')
                     }
                     renderValue={(v) =>
                        v === 'per_photo' ? 'Per Photo' : v === 'one_time' ? 'One-Time Fee' : 'Donation'
                     }
                     MenuProps={{
                        PaperProps: {
                           sx: {
                              maxHeight: 360,
                              bgcolor: 'background.paper',
                              border: 1,
                              borderColor: 'divider',
                              color: 'text.primary',
                              '& .MuiMenuItem-root': { color: 'text.primary' },
                           },
                        },
                     }}
                  >
                     <MenuItem value='per_photo'>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <MoneyIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                           <Box>
                              <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
                                 Per Photo
                              </Typography>
                              <Typography variant='caption' color='text.secondary'>
                                 Users pay for each photo they download
                              </Typography>
                           </Box>
                        </Box>
                     </MenuItem>
                     <MenuItem value='one_time'>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <LockIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                           <Box>
                              <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
                                 One-Time Fee
                              </Typography>
                              <Typography variant='caption' color='text.secondary'>
                                 Pay once to access all photos
                              </Typography>
                           </Box>
                        </Box>
                     </MenuItem>
                     <MenuItem value='donation'>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <FavoriteIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                           <Box>
                              <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
                                 Donation
                              </Typography>
                              <Typography variant='caption' color='text.secondary'>
                                 Members can donate any amount
                              </Typography>
                           </Box>
                        </Box>
                     </MenuItem>
                  </Select>
               </FormControl>

               <Box sx={{ mb: 3 }}>
                  <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                     {pricingModel === 'per_photo'
                        ? 'Price Per Photo'
                        : pricingModel === 'one_time'
                          ? 'Album Access Fee'
                          : 'Suggested Donation Amount'}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 2, fontSize: '0.875rem' }}>
                     {pricingModel === 'per_photo'
                        ? 'Set the price that users will pay to download each photo without watermark.'
                        : pricingModel === 'one_time'
                          ? 'Set the one-time fee for unlimited access to all photos in the album.'
                          : 'Set a suggested amount. Members will choose their own donation amount when donating.'}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <Typography variant='h6' sx={{ fontSize: '1.25rem', color: 'text.primary' }}>
                        $
                     </Typography>
                     <TextField
                        value={pricePerPhoto}
                        onChange={(e) => setPricePerPhoto(e.target.value)}
                        sx={{ width: 120 }}
                        size='small'
                     />
                     <Typography variant='body2' color='text.secondary'>
                        USD
                     </Typography>
                  </Box>
               </Box>
            </Box>
         );
      }
   }

   return null;
};

export default CreateAlbumWizardPanels;
